import EnLayout from '@/components/feature/EnLayout';
import useSEO from '@/hooks/useSEO';
import { Link } from 'react-router-dom';

export default function EnTermsOfUsePage() {
  useSEO({
    title: 'Terms of Use | Kang Yong Biotech Ltd.',
    description: 'Kang Yong Biotech website terms of use covering intellectual property, prohibited uses, disclaimer of liability, inquiry form policy, and governing law.',
    keywords: 'terms of use, website terms, Kang Yong Biotech, disclaimer, intellectual property',
    lang: 'en',
    canonicalPath: '/en/terms-of-use',
  });

  return (
    <EnLayout>
      <div className="min-h-screen bg-white pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
            <Link to="/en" className="hover:text-gray-600 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-base" />
            <span className="text-gray-600">Terms of Use</span>
          </nav>

          {/* Header */}
          <div className="mb-10 pb-8 border-b border-gray-100">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Terms of Use</h1>
            <p className="text-gray-400 text-sm">Last updated: January 2025</p>
          </div>

          {/* Content */}
          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
              <p className="text-[0.9375rem]">
                Welcome to the website of Kang Yong Biotech Ltd. (hereinafter "the Company"). By accessing or using this website,
                you agree to be bound by these Terms of Use. If you do not agree, please discontinue use of this website.
                The Company reserves the right to modify these terms at any time, with changes effective upon publication.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Purpose of the Website</h2>
              <p className="text-[0.9375rem] mb-3">This website is intended to provide the following information and services:</p>
              <ul className="list-disc list-inside space-y-2 text-[0.9375rem] pl-2">
                <li>Company introduction, business scope, and service overview</li>
                <li>OEM/ODM manufacturing services and related information</li>
                <li>Business inquiry forms and contact functionality</li>
                <li>Industry knowledge and regulatory information for health supplements</li>
              </ul>
              <p className="text-[0.9375rem] mt-3">
                Website content is provided for commercial inquiry and informational purposes only,
                and does not constitute legal, medical, or health advice.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Intellectual Property</h2>
              <p className="text-[0.9375rem]">
                All content on this website, including but not limited to text, images, icons, logos, designs, code, and layout,
                is protected by copyright and other intellectual property laws, with ownership retained by the Company.
                No content may be reproduced, distributed, modified, displayed, or used without prior written authorization from the Company.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Prohibited Uses</h2>
              <p className="text-[0.9375rem] mb-3">When using this website, you agree not to:</p>
              <ul className="list-disc list-inside space-y-2 text-[0.9375rem] pl-2">
                <li>Interfere with or disrupt the normal operation of the website</li>
                <li>Use the website to send spam or unauthorized commercial communications</li>
                <li>Submit fraudulent or misleading inquiries through the contact forms</li>
                <li>Attempt unauthorized access to the website&apos;s systems or databases</li>
                <li>Scrape, crawl, or automatically extract website content in bulk</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Accuracy of Information</h2>
              <p className="text-[0.9375rem]">
                While the Company strives to ensure the accuracy and timeliness of website information,
                we do not guarantee that all content is complete, current, or error-free.
                Product specifications, service details, and formula information are provided for reference only;
                actual cooperation details are governed by written agreements between parties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Disclaimer of Liability</h2>
              <p className="text-[0.9375rem]">
                To the fullest extent permitted by law, the Company shall not be liable for any direct or indirect losses
                arising from the use of this website. This website may contain links to third-party websites;
                the Company assumes no responsibility for third-party content, and users access such sites at their own risk.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Inquiry Form Policy</h2>
              <p className="text-[0.9375rem]">
                By submitting an inquiry form, you agree that the information provided is truthful and accurate.
                The Company will handle your personal data in accordance with our Privacy Policy.
                The Company reserves the right to decline or disregard any inquiry that is clearly made in bad faith
                or is inconsistent with the nature of our business.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Governing Law and Jurisdiction</h2>
              <p className="text-[0.9375rem]">
                These Terms of Use shall be governed by and construed in accordance with the laws of the Republic of China (Taiwan).
                Any disputes arising from these terms or use of this website shall be subject to the exclusive jurisdiction
                of the Taichung District Court of Taiwan as the court of first instance.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Contact Us</h2>
              <p className="text-[0.9375rem]">For any questions regarding these Terms of Use, please contact us:</p>
              <div className="mt-4 bg-gray-50 rounded-lg p-5 space-y-2 text-[0.9375rem]">
                <p><strong className="text-gray-900">Kang Yong Biotech Ltd.</strong></p>
                <p className="text-gray-600">6F, No. 79, Sec. 1, Zhongqing Rd., North Dist., Taichung City, Taiwan</p>
                <p>
                  Phone:{' '}
                  <a href="tel:+886422029858" className="text-[#2d5a27] hover:underline cursor-pointer">+886-4-2202-9858</a>
                </p>
                <p>
                  Email:{' '}
                  <a href="mailto:natural1070606@gmail.com" className="text-[#2d5a27] hover:underline cursor-pointer">natural1070606@gmail.com</a>
                </p>
              </div>
            </section>
          </div>

          {/* Related legal links */}
          <div className="mt-12 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <Link
              to="/en"
              className="inline-flex items-center gap-2 text-[#2d5a27] hover:text-[#1a3d18] text-sm font-medium transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              Back to Home
            </Link>
            <Link
              to="/en/privacy-policy"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#2d5a27] text-sm transition-colors cursor-pointer"
            >
              <i className="ri-shield-check-line" />
              View Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </EnLayout>
  );
}
